import clsx from 'clsx';
import type * as React from 'react';

import type { InlineProps } from '@gitbook/slate-react';
import {
    type OverlayTriggerState,
    Popover,
    PopoverRoot,
    Tooltip,
    useDoubleClick,
    usePopoverTriggerRef,
    usePopoverTriggerState,
} from '@gitbook/spine-react';

import { useRestoreFocusFromPopover } from './useRestoreFocusFromPopover';
import { VoidInlineFrame } from './VoidInlineFrame';

/**
 * Whether an hover highlighting should be shown.
 * - `false` means no highlighting.
 * - `inline` means highlighting it as inline text.
 * - `inline-block` means highlighting the inline as containing elements that are inline-block.
 */
type VoidInlineWithPopoverHoverHighlighting = false | 'inline' | 'inline-block';

type VoidInlineWithPopoverRenderSpan = (props: {
    /** Whether the popover is open. */
    isActive: boolean;
    /** Whether the inline is focused. */
    isFocused: boolean;
    /** Whether the inline is selected. */
    isSelected: boolean;
    /** Open the popover. */
    openPopover: () => void;
}) => React.ReactNode;

type VoidInlineWithPopoverProps = InlineProps & {
    /** Render the span inside the inline. */
    renderSpan: VoidInlineWithPopoverRenderSpan;
    /** Render the content inside the popover. */
    popover: React.ReactNode;
    /**
     * Whether an hover highlighting should be shown.
     * @default 'inline-block'
     */
    hoverHighlighting?: VoidInlineWithPopoverHoverHighlighting;
};

/**
 * Render an inline void with a popover to edit it.
 */
export function VoidInlineWithPopover(props: VoidInlineWithPopoverProps) {
    const { renderSpan, popover, hoverHighlighting = 'inline-block', ...rest } = props;
    const { editor } = rest;

    if (editor.readOnly) {
        return (
            <VoidInlineFrame {...rest} className="group/voidinline overflow-hidden align-middle">
                {renderSpan({
                    isActive: false,
                    isFocused: false,
                    isSelected: false,
                    openPopover: () => {},
                })}
            </VoidInlineFrame>
        );
    }

    return <VoidInlineWithPopoverEditable {...props} hoverHighlighting={hoverHighlighting} />;
}

function VoidInlineWithPopoverEditable(
    props: VoidInlineWithPopoverProps & {
        hoverHighlighting: VoidInlineWithPopoverHoverHighlighting;
    }
) {
    const { renderSpan, popover, hoverHighlighting, ...rest } = props;
    const { node, editor } = props;

    const trigger = usePopoverTriggerState();

    // When closing the popover, focus the editor.
    useRestoreFocusFromPopover(trigger, editor, node);

    return (
        <PopoverRoot {...trigger.rootProps}>
            <VoidInlineWithPopoverSpan
                {...rest}
                renderSpan={renderSpan}
                trigger={trigger}
                hoverHighlighting={hoverHighlighting}
            />
            <Popover placement="bottom-start" className="p-2">
                {popover}
            </Popover>
        </PopoverRoot>
    );
}

function VoidInlineWithPopoverSpan(
    props: InlineProps & {
        trigger: OverlayTriggerState;
        renderSpan: VoidInlineWithPopoverRenderSpan;
        hoverHighlighting: VoidInlineWithPopoverHoverHighlighting;
    }
) {
    const { trigger, renderSpan, hoverHighlighting, ...rest } = props;
    const { node, editor, isFocused, isSelected } = rest;

    const ref = usePopoverTriggerRef();
    const openPopover = () => {
        editor.change((change) => {
            change.collapseToStartOf(node);
            change.blur();
        });

        trigger.open();
    };

    const onClick = useDoubleClick((event) => {
        event.preventDefault();
        openPopover();
    });

    return (
        <VoidInlineFrame
            ref={ref}
            {...rest}
            isFocused={trigger.isOpen ? false : isFocused}
            isSelected={trigger.isOpen ? false : isSelected}
            onClick={onClick}
            onContextMenu={(event) => {
                event.preventDefault();
                openPopover();
            }}
            className="group/voidinline cursor-pointer align-middle"
        >
            <Tooltip
                label="Double or right click to edit"
                delay={800}
                offset={hoverHighlighting === 'inline-block' ? 8 : 4}
            >
                <span className="relative inline-flex">
                    {hoverHighlighting !== false ? (
                        <span
                            className={clsx(
                                '-inset-x-1 absolute rounded',
                                hoverHighlighting === 'inline' ? 'inset-y-0' : '-inset-y-1',
                                trigger.isOpen
                                    ? 'bg-block-highlight'
                                    : isSelected
                                      ? 'bg-block-highlight'
                                      : 'group-active/voidinline:-inset-0.5 group-hover/voidinline:bg-inactive group-active/voidinline:bg-inactive'
                            )}
                        />
                    ) : null}
                    <span className="relative z-10 inline-flex">
                        {renderSpan({
                            isActive: trigger.isOpen,
                            isFocused,
                            isSelected,
                            openPopover,
                        })}
                    </span>
                </span>
            </Tooltip>
        </VoidInlineFrame>
    );
}
